import React, { FC, useEffect, useRef, useState } from "react"
import { SearchComponent } from "../../../assets/ts/components"
import { KTIcon } from "../../../helpers"
import LibrarySearch from "src/app/modules/library/components/mainSearch"
import { listSimpleSearch } from "src/app/modules/library/components/_requests"
import { Link } from "react-router-dom"

import { useLocation } from "react-router-dom"



interface SearchResult {
  recordId: string
  libraryName: string
  [key: string]: any
  lastModifiedDate: string
}

const Search: FC = () => {
  const [menuState] = useState<"main" | "advanced" | "preferences">("main")
  const [searchQuery, setSearchQuery] = useState<string>("")
  const [showLibrarySearch, setShowLibrarySearch] = useState<boolean>(false)
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState<boolean>(false)

  const element = useRef<HTMLDivElement | null>(null)
  const wrapperElement = useRef<HTMLDivElement | null>(null)
  const resultsElement = useRef<HTMLDivElement | null>(null)
  const suggestionsElement = useRef<HTMLDivElement | null>(null)
  const emptyElement = useRef<HTMLDivElement | null>(null)
  const searchObjectRef = useRef<SearchComponent | null>(null)

  const fetchSearchResults = async (query: string) => {
    if (!query || query.length < 2) {
      setSearchResults([])
      resultsElement.current?.classList.add("d-none")
      emptyElement.current?.classList.remove("d-none")
      return
    }

    setLoading(true)
    try {
      const response = await listSimpleSearch({
        rawCondition: query,
        pageNumber: 1,
      })
      if (response?.data) {
        setSearchResults(response.data)
        resultsElement.current?.classList.remove("d-none")
        emptyElement.current?.classList.add("d-none")
      } else {
        setSearchResults([])
        resultsElement.current?.classList.add("d-none")
        emptyElement.current?.classList.remove("d-none")
      }
    } catch (err) {
      console.error("Error fetching search results:", err)
      setSearchResults([])
      resultsElement.current?.classList.add("d-none")
      emptyElement.current?.classList.remove("d-none")
    } finally {
      setLoading(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault()
      if (searchQuery.trim()) {
        suggestionsElement.current?.classList.add("d-none")
        resultsElement.current?.classList.remove("d-none")
        emptyElement.current?.classList.add("d-none")
        fetchSearchResults(searchQuery.trim())
        setShowLibrarySearch(true)
      }
    }
  }

  useEffect(() => {
    const searchObject = SearchComponent.createInsance("#kt_header_search")
    if (searchObject) {
      searchObjectRef.current = searchObject

      searchObject.on("kt.search.process", function (this: SearchComponent) {
        setTimeout(() => {
          suggestionsElement.current?.classList.add("d-none")

          const query = this.getQuery()
          if (!query || query.length < 2) {
            this.clear()
            return
          }

          resultsElement.current?.classList.remove("d-none")
          emptyElement.current?.classList.add("d-none")

          setSearchQuery(query)
          fetchSearchResults(query)
          setShowLibrarySearch(true)

          this.complete()
        }, 300)
      })

      searchObject.on("kt.search.clear", function (this: SearchComponent) {
        suggestionsElement.current?.classList.remove("d-none")
        resultsElement.current?.classList.add("d-none")
        emptyElement.current?.classList.add("d-none")
        setShowLibrarySearch(false)
        setSearchQuery("")
        setSearchResults([])
      })
    }

    return () => {}
  }, [])

  useEffect(() => {
    if (searchQuery.length === 0) {
      if (searchObjectRef.current) {
        searchObjectRef.current.clear()
      }
      return
    }

    const timeoutId = setTimeout(() => {
      if (searchQuery.length >= 2) {
        suggestionsElement.current?.classList.add("d-none")
        resultsElement.current?.classList.remove("d-none")
        emptyElement.current?.classList.add("d-none")
        fetchSearchResults(searchQuery)
        setShowLibrarySearch(true)
      }
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [searchQuery])


    const location = useLocation() as any

  useEffect(() => {
    if (location.state?.resetSearch) {
      setSearchQuery("")
      setSearchResults([])
      setShowLibrarySearch(false)
      window.history.replaceState({}, document.title)
    }
  }, [location])

  return (
    <>
      <div
        id="kt_header_search"
        className="d-flex align-items-stretch"
        data-kt-search-keypress="true"
        data-kt-search-min-length="2"
        data-kt-search-enter="enter"
        data-kt-search-layout="menu"
        data-kt-menu-trigger="auto"
        data-kt-menu-overflow="false"
        data-kt-menu-permanent="true"
        data-kt-menu-placement="bottom-end"
        ref={element}
      >
        <div
          className="d-flex align-items-center"
          data-kt-search-element="toggle"
          id="kt_header_search_toggle"
        >
          <div className="btn btn-icon btn-active-light-primary w-30px h-30px w-md-40px h-md-40px">
            <KTIcon iconName="magnifier" className="fs-1" />
          </div>
        </div>

        <div
          data-kt-search-element="content"
          className="menu menu-sub menu-sub-dropdown p-7 w-325px w-md-375px"
        >
          <div
            className={`${menuState === "main" ? "" : "d-none"}`}
            ref={wrapperElement}
            data-kt-search-element="wrapper"
          >
            <form
              data-kt-search-element="form"
              className="w-100 position-relative mb-3"
              autoComplete="off"
              onSubmit={(e) => e.preventDefault()}
            >
              <KTIcon
                iconName="magnifier"
                className="fs-2 text-lg-1 text-gray-500 position-absolute top-50 translate-middle-y ms-0"
              />

              <input
                type="text"
                className="form-control form-control-flush ps-10"
                name="search"
                placeholder="Search..."
                data-kt-search-element="input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </form>

            {/* Results */}
            <div ref={resultsElement} data-kt-search-element="results" className="d-none">
              {loading ? (
                <div className="text-center py-5">
                  <span className="spinner-border text-primary" />
                </div>
              ) : (
                showLibrarySearch && searchResults.length > 0 && (
                  <div className="mt-3" style={{ maxHeight: "400px", overflowY: "auto" }}>
                    {/* 🔹 Link to full page */}
                    <Link
                      to="library/search_results"
                      state={{ results: searchResults }}
                      className="btn btn-sm btn-primary w-100 mb-2"
                    >
                      View All Results
                    </Link>
                  </div>
                )
              )}
            </div>

            {/* Empty */}
            <div ref={emptyElement} data-kt-search-element="empty" className="d-none">
              <div className="text-center pt-7 pb-5">
                <KTIcon iconName="search-list" className="fs-6x opacity-10" />
                <div className="fw-bold text-gray-600 mt-5">No results found</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export { Search }
